import { z } from "zod";

/**
 * Shared vocabulary and API shapes for the payment vault.
 *
 * The vault stores only opaque Stripe identifiers plus display metadata
 * (brand, last4, expiry). Card numbers and CVCs live exclusively in Stripe;
 * nothing in these types can carry one, and nothing may be added here that
 * could.
 *
 * Like billing subscriptions, the wallet subject is the *user* (the person),
 * not the account.
 */

/**
 * `deleted` mirrors Stripe's `customer.deleted`: the Stripe Customer is gone
 * (dashboard deletion or GDPR erasure), so the wallet must stop being used,
 * but the row is retained so the customer→user mapping survives for any
 * late-arriving events referencing the dead customer.
 */
export const WALLET_STATUSES = ["active", "deleted"] as const;
export type WalletStatus = (typeof WALLET_STATUSES)[number];

/**
 * Detached rows are retained rather than deleted so an out-of-order
 * `payment_method.attached` delivered after the detach has a row (with a
 * newer `providerEventAt`) to lose against, instead of a void to re-insert
 * into. Retention only has to outlast the provider's redelivery horizon, so
 * the reaper prunes rows past that.
 */
export const PAYMENT_METHOD_STATUSES = ["active", "detached"] as const;
export type PaymentMethodStatus = (typeof PAYMENT_METHOD_STATUSES)[number];

/**
 * Stripe's per-method consent marker, mirrored verbatim. We save cards with
 * `always` (the user explicitly saves a card for future okthx charges), but
 * the mirror must be able to represent methods that arrived with other
 * values — e.g. created from the dashboard or by an older policy.
 */
export const ALLOW_REDISPLAY_VALUES = ["always", "limited", "unspecified"] as const;
export type AllowRedisplayValue = (typeof ALLOW_REDISPLAY_VALUES)[number];

/**
 * Lifecycle of a received webhook event. `processing` is a mutual-exclusion
 * claim: Cloud Tasks delivers at-least-once, and two live attempts must not
 * dispatch the same event concurrently. `skipped` is a terminal success for
 * events we deliberately did not apply (unknown customer, stale ordering,
 * subject deleted mid-flight); `failed` marks handler errors, preserved with
 * the payload for replay once the bug is fixed.
 */
export const STRIPE_WEBHOOK_EVENT_STATUSES = [
  "received",
  "processing",
  "processed",
  "skipped",
  "failed",
] as const;
export type StripeWebhookEventStatus = (typeof STRIPE_WEBHOOK_EVENT_STATUSES)[number];

/**
 * A saved payment method as the client sees it. `id` is our row uuid — the
 * Stripe `pm_…` id never leaves the server, so the API surface cannot be
 * replayed against Stripe directly.
 */
export const paymentMethodResp = z.object({
  id: z.string(),
  brand: z.string(),
  last4: z.string(),
  expMonth: z.number().int(),
  expYear: z.number().int(),
  isDefault: z.boolean(),
});

export const paymentMethodListResp = z.object({
  methods: z.array(paymentMethodResp),
});

/**
 * Removing the last method while a subscription is active silently breaks the
 * next renewal, so the server refuses unless the client passes `confirm`
 * after showing the hard warning. The 409 body carries this reason.
 */
export const PAYMENT_METHOD_DELETE_BLOCKED_REASON = "last_method_on_active_subscription";

export const paymentMethodDeleteReq = z.object({
  /** Acknowledges the last-method warning; see PAYMENT_METHOD_DELETE_BLOCKED_REASON. */
  confirm: z.boolean().optional(),
});

export const setupIntentResp = z.object({
  clientSecret: z.string(),
});

export type PaymentMethodResp = z.infer<typeof paymentMethodResp>;
export type PaymentMethodListResp = z.infer<typeof paymentMethodListResp>;
export type PaymentMethodDeleteReq = z.infer<typeof paymentMethodDeleteReq>;
export type SetupIntentResp = z.infer<typeof setupIntentResp>;
